@if (searchVisible()) {
    <div
        class="my-4 flex"
        [class.justify-start]="searchPosition() === HorizontalPosition.START"
        [class.justify-center]="searchPosition() === HorizontalPosition.CENTER"
        [class.justify-end]="searchPosition() === HorizontalPosition.END">
        <mat-form-field>
            <mat-label>{{ 'shared.faq.search' | translate }}</mat-label>
            <mat-icon matPrefix>search</mat-icon>
            <input matInput type="text" [formControl]="faqSearch" />
            @if (faqSearch.value) {
                <button
                    matSuffix
                    mat-icon-button
                    (click)="resetFilter()"
                    attr.aria-label="{{
                        'shared.faq.resetSearchFilter' | translate
                    }}">
                    <mat-icon>cancel</mat-icon>
                </button>
            }
        </mat-form-field>
        <div class="sr-only" aria-live="polite">
            @if (faqSearch.value) {
                @if (filteredFaqs().length === 0) {
                    {{ 'shared.faq.noFAQsFiltered' | translate }}
                } @else {
                    {{
                        'shared.faq.searchResults'
                            | translate: { count: filteredFaqs().length }
                    }}
                }
            } @else {
                {{
                    'shared.faq.searchResults'
                        | translate: { count: faqs().length }
                }}
            }
        </div>
    </div>
}
@let dragDropDisabled =
    !isAdmin() || !!faqSearch.value || orderUpdateInProgress();
<mat-accordion
    multi
    cdkDropList
    [cdkDropListData]="filteredFaqs()"
    [cdkDropListDisabled]="dragDropDisabled"
    (cdkDropListDropped)="dropFaq($event)">
    @for (faq of filteredFaqs(); track faq.id; let i = $index) {
        @let faqBusy = performingActionFaqIds().get(faq.id);
        <mat-expansion-panel
            cdkDrag
            [cdkDragDisabled]="dragDropDisabled"
            [cdkDragData]="faq"
            [expanded]="expandedFaqIds().has(faq.id)"
            (opened)="openFaq(faq.id)"
            (closed)="closeFaq(faq.id)">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <!-- Setting min width enables the flex item to shrink on small screens, enabling the word break -->
                    @if (isAdmin()) {
                        <apcam-tooltip-custom
                            customMatTooltip="{{ getReorderTooltip() }}"
                            [customMatTooltipDisabled]="!dragDropDisabled">
                            <span
                                cdkDragHandle
                                class="flex shrink-0 h-10 w-10 items-center justify-center me-2 cursor-grab active:cursor-grabbing"
                                aria-hidden="true">
                                <mat-icon>drag_handle</mat-icon>
                            </span>
                        </apcam-tooltip-custom>
                    }
                    <h2
                        class="min-w-0 text-base! text-inherit! [font-family:inherit]!">
                        @if (faq.question.translations[currLang()]) {
                            <apcam-translation [model]="faq.question" />
                        } @else if (faq.question.translations[defaultLang]) {
                            {{ faq.question.translations[defaultLang] }}
                        } @else {
                            {{ missingTranslationKey() | translate }}
                        }
                    </h2>
                    @if (!hasAllTranslations(faq) && isAdmin()) {
                        <apcam-tooltip-custom
                            customMatTooltip="{{
                                missingTranslationKey() | translate
                            }}">
                            <mat-icon color="warn" class="ms-4">
                                language
                            </mat-icon>
                        </apcam-tooltip-custom>
                    }
                </mat-panel-title>
            </mat-expansion-panel-header>
            <!-- Expansion panel body when the panel is opened -->
            <div class="@container mt-4">
                @if (faq.answer.translations[currLang()]) {
                    <div
                        class="faq-answer"
                        [innerHTML]="faq.answer.translations[currLang()]"></div>
                } @else if (faq.answer.translations[defaultLang]) {
                    <div
                        class="faq-answer"
                        [innerHTML]="
                            faq.answer.translations[defaultLang]
                        "></div>
                } @else {
                    <p>
                        {{ missingTranslationKey() | translate }}
                    </p>
                }
                <!-- Action buttons are visible only when 'isAdmin' flag is set -->
                @if (isAdmin()) {
                    <div class="flex items-center justify-end gap-2">
                        <apcam-tooltip-custom
                            customMatTooltip="{{
                                getReorderTooltip('shared.faq.moveUp')
                            }}">
                            <button
                                mat-icon-button
                                [disabled]="
                                    dragDropDisabled || i === 0 || faqBusy
                                "
                                (click)="moveFaqUp(faq)"
                                attr.aria-label="{{
                                    'shared.faq.moveUp' | translate
                                }}">
                                <mat-icon>arrow_upward</mat-icon>
                            </button>
                        </apcam-tooltip-custom>
                        <apcam-tooltip-custom
                            customMatTooltip="{{
                                getReorderTooltip('shared.faq.moveDown')
                            }}">
                            <button
                                mat-icon-button
                                [disabled]="
                                    dragDropDisabled ||
                                    i === filteredFaqs().length - 1 ||
                                    faqBusy
                                "
                                (click)="moveFaqDown(faq)"
                                attr.aria-label="{{
                                    'shared.faq.moveDown' | translate
                                }}">
                                <mat-icon>arrow_downward</mat-icon>
                            </button>
                        </apcam-tooltip-custom>
                        <div class="hidden gap-2 @2xl:flex">
                            <button
                                mat-stroked-button
                                color="warn"
                                [disabled]="orderUpdateInProgress() || faqBusy"
                                [attr.aria-busy]="faqBusy ? true : null"
                                [attr.aria-label]="
                                    faqBusy
                                        ? ('shared.faq.deleting' | translate)
                                        : null
                                "
                                (click)="deleteFaq(faq)">
                                @if (!faqBusy) {
                                    <mat-icon aria-hidden="true">
                                        delete
                                    </mat-icon>
                                } @else {
                                    <mat-icon>
                                        <apcam-loading-spinner
                                            color="warn"
                                            size="xs" />
                                    </mat-icon>
                                }
                                {{ 'shared.buttons.delete' | translate }}
                            </button>
                            <button
                                mat-flat-button
                                color="primary"
                                [disabled]="orderUpdateInProgress() || faqBusy"
                                [attr.aria-busy]="faqBusy ? true : null"
                                [attr.aria-label]="
                                    faqBusy
                                        ? ('shared.faq.editing' | translate)
                                        : null
                                "
                                (click)="editFaq(faq)">
                                @if (!faqBusy) {
                                    <mat-icon aria-hidden="true">
                                        edit
                                    </mat-icon>
                                } @else {
                                    <mat-icon>
                                        <apcam-loading-spinner
                                            color="warn"
                                            size="xs" />
                                    </mat-icon>
                                }
                                {{ 'shared.buttons.edit' | translate }}
                            </button>
                        </div>
                        <div class="flex gap-2 @2xl:hidden">
                            <apcam-tooltip-custom
                                customMatTooltip="{{
                                    'shared.buttons.delete' | translate
                                }}">
                                <button
                                    mat-icon-button
                                    color="warn"
                                    [disabled]="
                                        orderUpdateInProgress() || faqBusy
                                    "
                                    [attr.aria-busy]="faqBusy ? true : null"
                                    (click)="deleteFaq(faq)"
                                    [attr.aria-label]="
                                        (faqBusy
                                            ? 'shared.faq.deleting'
                                            : 'shared.buttons.delete'
                                        ) | translate
                                    ">
                                    @if (!faqBusy) {
                                        <mat-icon aria-hidden="true">
                                            delete
                                        </mat-icon>
                                    } @else {
                                        <mat-icon>
                                            <apcam-loading-spinner
                                                color="warn"
                                                size="sm" />
                                        </mat-icon>
                                    }
                                </button>
                            </apcam-tooltip-custom>
                            <apcam-tooltip-custom
                                customMatTooltip="{{
                                    'shared.buttons.edit' | translate
                                }}">
                                <button
                                    mat-icon-button
                                    color="primary"
                                    [disabled]="
                                        orderUpdateInProgress() || faqBusy
                                    "
                                    [attr.aria-busy]="faqBusy ? true : null"
                                    (click)="editFaq(faq)"
                                    [attr.aria-label]="
                                        (faqBusy
                                            ? 'shared.faq.editing'
                                            : 'shared.buttons.edit'
                                        ) | translate
                                    ">
                                    @if (!faqBusy) {
                                        <mat-icon aria-hidden="true">
                                            edit
                                        </mat-icon>
                                    } @else {
                                        <mat-icon>
                                            <apcam-loading-spinner
                                                color="warn"
                                                size="sm" />
                                        </mat-icon>
                                    }
                                </button>
                            </apcam-tooltip-custom>
                        </div>
                    </div>
                }
            </div>
        </mat-expansion-panel>
    }
    <!-- No FAQs defined - Nonetheless, this should never happen, as we do not render a tab in main navigation-->
    <!-- However, user can navigate manually to 'faq' path -> for that case, render the error message -->
    @if (faqs().length === 0) {
        <p class="py-4 border border-gray-200 rounded-lg text-center">
            {{ 'shared.faq.noFAQsDefined' | translate }}
        </p>
    }
    <!-- No FAQs after filter is applied -  If the filter is active and no question matches the filter -->
    @if (filteredFaqs().length === 0 && faqSearch.value !== '') {
        <p class="py-4 border border-gray-200 rounded-lg text-center">
            {{ 'shared.faq.noFAQsFiltered' | translate }}
        </p>
    }
</mat-accordion>
